/**
 * PWAInstallBanner — Bannière dismissable suggérant l'installation PWA.
 *
 * Affichée sous le header une fois l'utilisateur connecté, jusqu'à ce qu'il
 * la ferme ou installe l'app. Le dismiss est persisté en localStorage — on
 * ne re-propose pas à chaque session.
 *
 * Masquée si :
 *   - l'app tourne déjà en mode standalone
 *   - l'utilisateur a déjà cliqué sur fermer (flag localStorage)
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePWAInstall from '../hooks/usePWAInstall';

const DISMISS_KEY = 'ekynair_pwa_banner_dismissed';

const PWAInstallBanner = ({ darkMode = false, onOpenInstructions }) => {
  const { t } = useTranslation();
  const { canInstall, isStandalone, platform, install } = usePWAInstall();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (isStandalone || dismissed) return null;

  // Sur Firefox Desktop l'installation n'est pas supportée — inutile de proposer.
  if (platform === 'firefox-desktop') return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* quota */ }
    setDismissed(true);
  };

  const handleClick = async () => {
    if (canInstall) {
      const outcome = await install();
      if (outcome === 'accepted') dismiss();
    } else {
      // Pas de prompt natif (iOS, Firefox Android, desktop sans prompt) →
      // renvoyer vers la page d'instructions détaillées si disponible.
      onOpenInstructions?.();
    }
  };

  const c = {
    text:    darkMode ? '#E1E1E1' : '#2C3E50',
    textSec: darkMode ? '#B8BFC7' : '#6B7280',
    bg:      darkMode ? 'rgba(27,119,241,0.12)' : '#EAF3FF',
    border:  darkMode ? 'rgba(27,119,241,0.35)' : '#BFD9FB',
    accent:  '#1B77F1',
  };

  const ctaLabel = canInstall ? t('app.pwa.install') : t('app.pwa.howTo');

  return (
    <div role="region" aria-label={t('app.pwa.aria')}
      style={{ display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 16px', marginBottom: '16px',
        background: c.bg, border: `1px solid ${c.border}`, borderRadius: '12px',
        fontFamily: "'Inter', sans-serif" }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c.accent}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: c.text, marginBottom: '2px' }}>
          {t('app.pwa.title')}
        </div>
        <div style={{ fontSize: '13px', color: c.textSec, lineHeight: '1.4' }}>
          {t('app.pwa.body')}
        </div>
      </div>
      <button type="button" onClick={handleClick}
        style={{ padding: '8px 14px', borderRadius: '8px', border: 'none',
          background: c.accent, color: '#fff', cursor: 'pointer', fontSize: '13px',
          fontWeight: '600', fontFamily: "'Inter', sans-serif", flexShrink: 0 }}>
        {ctaLabel}
      </button>
      <button type="button" onClick={dismiss} aria-label={t('app.pwa.dismiss')}
        style={{ padding: '4px', borderRadius: '6px', border: 'none',
          background: 'transparent', color: c.textSec, cursor: 'pointer',
          display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
};

export default PWAInstallBanner;
